/**
 * The API calls behind multipart uploads (see multipartUpload.ts), for library
 * and brief files and for editors' revisions.
 *
 * Kept free of app imports (Apollo, Firebase) so the upload engine can be
 * exercised on its own: the caller passes the GraphQL endpoint and a way to
 * get a current sign-in token (uploads can outlast the one-hour token).
 */

import {
  MultipartResumeState,
  MultipartTarget,
  MultipartUploadApi,
  PartUrlBatch,
  UploadApiError,
  UploadInPartsOptions,
  uploadInParts,
} from './multipartUpload';
import type { StoredAsset } from './r2AssetUpload';

export const CREATE_MULTIPART_ASSET_UPLOAD = `mutation CreateMultipartAssetUpload($kind: String!, $filename: String!, $contentType: String!, $fileSizeBytes: Float!, $partSizeBytes: Float) {
  createMultipartAssetUpload(kind: $kind, filename: $filename, contentType: $contentType, fileSizeBytes: $fileSizeBytes, partSizeBytes: $partSizeBytes) {
    objectKey uploadId partSizeBytes partCount expiresAt
  }
}`;

export const COMPLETE_MULTIPART_ASSET_UPLOAD = `mutation CompleteMultipartAssetUpload($kind: String!, $objectKey: String!, $uploadId: String!, $filename: String!, $contentType: String!, $fileSizeBytes: Float!) {
  completeMultipartAssetUpload(kind: $kind, objectKey: $objectKey, uploadId: $uploadId, filename: $filename, contentType: $contentType, fileSizeBytes: $fileSizeBytes) {
    url objectKey storageProvider filename contentType sizeBytes
  }
}`;

export const CREATE_MULTIPART_REVISION_UPLOAD = `mutation CreateMultipartRevisionUpload($requestId: String!, $filename: String!, $contentType: String!, $fileSizeBytes: Float!, $partSizeBytes: Float, $submitForReview: Boolean) {
  createMultipartRevisionUpload(requestId: $requestId, filename: $filename, contentType: $contentType, fileSizeBytes: $fileSizeBytes, partSizeBytes: $partSizeBytes, submitForReview: $submitForReview) {
    objectKey uploadId partSizeBytes partCount expiresAt
  }
}`;

export const COMPLETE_MULTIPART_REVISION_UPLOAD = `mutation CompleteMultipartRevisionUpload($requestId: String!, $objectKey: String!, $uploadId: String!, $filename: String!, $contentType: String!, $fileSizeBytes: Float!, $notes: String, $submitForReview: Boolean) {
  completeMultipartRevisionUpload(requestId: $requestId, objectKey: $objectKey, uploadId: $uploadId, filename: $filename, contentType: $contentType, fileSizeBytes: $fileSizeBytes, notes: $notes, submitForReview: $submitForReview) {
    id
    status
    revisionCount
    revisions { id filename createdBy createdByName createdAt status previewUrl notes sizeBytes }
  }
}`;

export const SIGN_MULTIPART_UPLOAD_PARTS = `mutation SignMultipartUploadParts($objectKey: String!, $uploadId: String!, $partNumbers: [Int!]!) {
  signMultipartUploadParts(objectKey: $objectKey, uploadId: $uploadId, partNumbers: $partNumbers) {
    expiresAt expiresInSeconds
    parts { partNumber url headers { name value } }
  }
}`;

export const RESUME_MULTIPART_UPLOAD = `mutation ResumeMultipartUpload($objectKey: String!, $uploadId: String!) {
  resumeMultipartUpload(objectKey: $objectKey, uploadId: $uploadId) {
    objectKey uploadId partSizeBytes partCount uploadedParts uploadedBytes status expiresAt
  }
}`;

export const ABORT_MULTIPART_UPLOAD = `mutation AbortMultipartUpload($objectKey: String!, $uploadId: String!) {
  abortMultipartUpload(objectKey: $objectKey, uploadId: $uploadId)
}`;

export interface GraphQLTransport {
  request<D>(query: string, variables: Record<string, unknown>): Promise<D>;
}

/** Posts GraphQL with a current token. Failures become UploadApiError, with the GraphQL error's code and reason. */
export function graphqlTransport(endpoint: string, getToken: () => Promise<string>): GraphQLTransport {
  return {
    async request<D>(query: string, variables: Record<string, unknown>): Promise<D> {
      const token = await getToken();
      let response: Response;
      try {
        response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ query, variables }),
        });
      } catch {
        throw new UploadApiError('PithPlay couldn’t be reached. Check your connection.', { network: true });
      }
      const body = await response.json().catch(() => null);
      const first = Array.isArray(body?.errors) ? body.errors[0] : null;
      if (first) {
        const extensions = first.extensions && typeof first.extensions === 'object' ? first.extensions : {};
        throw new UploadApiError(String(first.message || 'The upload didn’t finish. Try again.'), {
          code: typeof extensions.code === 'string' ? extensions.code : undefined,
          reason: typeof extensions.reason === 'string' ? extensions.reason : undefined,
          extensions,
          status: response.status,
        });
      }
      if (!response.ok || !body?.data) {
        throw new UploadApiError('The upload didn’t finish. Try again.', { status: response.status });
      }
      return body.data as D;
    },
  };
}

interface FileInfo {
  name: string;
  size: number;
}

/** The calls shared by every multipart upload (part URLs, resume, cancel). */
function sharedCalls(transport: GraphQLTransport) {
  const ids = (target: MultipartTarget) => ({ objectKey: target.objectKey, uploadId: target.uploadId });
  return {
    signParts: (target: MultipartTarget, partNumbers: number[]) =>
      transport
        .request<{ signMultipartUploadParts: PartUrlBatch }>(SIGN_MULTIPART_UPLOAD_PARTS, { ...ids(target), partNumbers })
        .then((data) => data.signMultipartUploadParts),
    resume: (target: MultipartTarget) =>
      transport
        .request<{ resumeMultipartUpload: MultipartResumeState }>(RESUME_MULTIPART_UPLOAD, ids(target))
        .then((data) => data.resumeMultipartUpload),
    abort: (target: MultipartTarget) =>
      transport
        .request<{ abortMultipartUpload: boolean }>(ABORT_MULTIPART_UPLOAD, ids(target))
        .then((data) => data.abortMultipartUpload),
  };
}

/** A library or brief file. */
export function assetUploadApi(
  transport: GraphQLTransport,
  file: FileInfo,
  kind: string,
  contentType: string,
): MultipartUploadApi<StoredAsset> {
  return {
    ...sharedCalls(transport),
    start: (partSizeBytes) =>
      transport
        .request<{ createMultipartAssetUpload: MultipartTarget }>(CREATE_MULTIPART_ASSET_UPLOAD, {
          kind,
          filename: file.name,
          contentType,
          fileSizeBytes: file.size,
          partSizeBytes,
        })
        .then((data) => data.createMultipartAssetUpload),
    complete: (target) =>
      transport
        .request<{ completeMultipartAssetUpload: StoredAsset }>(COMPLETE_MULTIPART_ASSET_UPLOAD, {
          kind,
          objectKey: target.objectKey,
          uploadId: target.uploadId,
          filename: file.name,
          contentType,
          fileSizeBytes: file.size,
        })
        .then((data) => data.completeMultipartAssetUpload),
  };
}

export interface RevisionUploadInput {
  requestId: string;
  contentType: string;
  notes?: string | null;
  submitForReview: boolean;
}

/** An editor's cut for a project. */
export function revisionUploadApi<P = any>(
  transport: GraphQLTransport,
  file: FileInfo,
  input: RevisionUploadInput,
): MultipartUploadApi<P> {
  return {
    ...sharedCalls(transport),
    start: (partSizeBytes) =>
      transport
        .request<{ createMultipartRevisionUpload: MultipartTarget }>(CREATE_MULTIPART_REVISION_UPLOAD, {
          requestId: input.requestId,
          filename: file.name,
          contentType: input.contentType,
          fileSizeBytes: file.size,
          partSizeBytes,
          submitForReview: input.submitForReview,
        })
        .then((data) => data.createMultipartRevisionUpload),
    complete: (target) =>
      transport
        .request<{ completeMultipartRevisionUpload: P }>(COMPLETE_MULTIPART_REVISION_UPLOAD, {
          requestId: input.requestId,
          objectKey: target.objectKey,
          uploadId: target.uploadId,
          filename: file.name,
          contentType: input.contentType,
          fileSizeBytes: file.size,
          notes: input.notes?.trim() ? input.notes.trim() : null,
          submitForReview: input.submitForReview,
        })
        .then((data) => data.completeMultipartRevisionUpload),
  };
}

type UploadFile = Blob & { name: string; lastModified?: number };
type EngineOptions = Omit<UploadInPartsOptions, 'scope'>;

export function uploadAssetInParts(
  file: UploadFile,
  kind: string,
  contentType: string,
  transport: GraphQLTransport,
  options: EngineOptions = {},
): Promise<StoredAsset> {
  return uploadInParts(file, assetUploadApi(transport, file, kind, contentType), { ...options, scope: `asset:${kind}` });
}

export function uploadRevisionInParts<P = any>(
  file: UploadFile,
  input: RevisionUploadInput,
  transport: GraphQLTransport,
  options: EngineOptions = {},
): Promise<P> {
  return uploadInParts(file, revisionUploadApi<P>(transport, file, input), { ...options, scope: `revision:${input.requestId}` });
}
